import { AfterViewInit, Component, EventEmitter, inject, OnInit, Output } from '@angular/core'
import { NgClass, NgForOf, NgIf } from '@angular/common'
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { TypeReferenceDto } from '../../../sunleon/data-access/http/dto/type-reference-dto'
import { ReferenceService } from '../../../sunleon/data-access/http/reference.service'
import { CalepinageService } from '../../data-access/calepinage.service'
import { GeomapDomainService } from '../../domain/geomap.domain'
import { ZoneMeasures } from '../../model/zone-measures'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'app-form-zone1',
  templateUrl: './form-zone1.component.html',
  styleUrls: ['./form-zone1.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, NgForOf, ReactiveFormsModule, NgClass],
})
export class FormZone1Component implements OnInit, AfterViewInit {
  @Output()
  formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>()

  private readonly geomapDomainService = inject(GeomapDomainService)
  private readonly calepinageService = inject(CalepinageService)
  readonly measuresZone1$ = this.geomapDomainService.getAllMeasuresForZone$(0)

  azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"

  zone1Form: FormGroup
  typePoses: TypeReferenceDto[] = []
  typeToitures: TypeReferenceDto[] = []
  typePlaces: TypeReferenceDto[] = []
  typeParkings: TypeReferenceDto[] = []

  ngOnInit(): void {
    this.referenceService.getTypePose().subscribe((data) => {
      this.typePoses = data
    })
    this.referenceService.getTypeToiture().subscribe((data) => {
      this.typeToitures = data.filter(t => t.code !== 'BAC_ACIER_SEC')
    })
    this.referenceService.getTypeParking().subscribe((data) => {
      this.typeParkings = data
    })
    this.referenceService.getTypePlace().subscribe((data) => {
      this.typePlaces = data
    })
    this.updateZoneValueValidators()
    this.zone1Form.updateValueAndValidity({ emitEvent: false })
    this.formReady.emit(this.zone1Form)
  }
  ngAfterViewInit(): void {
    this.measuresZone1$.subscribe((zoneMeasures: ZoneMeasures) => {
      this.zone1Form.get('surfaceZone1').patchValue(zoneMeasures.surface === 0 ? null : zoneMeasures.surface)
      this.zone1Form.get('azimutZone1').patchValue(zoneMeasures.azimuth === 0 ? null : zoneMeasures.azimuth)
      this.zone1Form.get('polygonsZone1').patchValue(zoneMeasures.polygons);
      this.zone1Form.get('azimutCoordinateZone1').patchValue(zoneMeasures.azimutCoordinates)
      if ((zoneMeasures.polygons?.length ?? 0) > 0 && this.zone1Form.get('puissanceChoisieZone1')?.value != null) {
        this.zone1Form.get('puissanceChoisieZone1')?.patchValue(null, { emitEvent: false })
      }
      this.updateZoneValueValidators()
      this.zone1Form.updateValueAndValidity({ emitEvent: false })
    })

    this.calepinageService.registerZoneAutoTrigger(0, this.zone1Form, {
      typePose: 'typePoseZone1',
      typeToiture: 'typeToitureZone1',
      inclinaison: 'inclinaisonZone1',
      typeVehiculeParking: 'typeVehiculeParkingZone1',
      typePlaceParking: 'typePlaceParkingZone1',
      orientation: 'orientationTerrasseZone1',
    })
  }

  onRadioClickZone1(code: string | null, controleName: string) {
    this.zone1Form.get(controleName).setValue(code)
    this.geomapDomainService.selectTypePoseOutils(0)
    this.geomapDomainService.selectionBarreOutils()
    this.geomapDomainService.selectionDessinOutilsSeulementZone()
  }

  constructor(private formBuilder: FormBuilder, private referenceService: ReferenceService, private modalService: NgbModal) {
    this.zone1Form = this.formBuilder.group({
      typePoseZone1: [null, [Validators.required]],
      surfaceZone1: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      azimutZone1: [null, [Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')]],
      typeToitureZone1: [''],
      orientationTerrasseZone1: ['SUD'],
      pvReadyZone1: [false],
      inclinaisonZone1: [null, [Validators.pattern('^\\d+$'), this.inclinaisonValidator()]],
      typeVehiculeParkingZone1: [''],
      typePlaceParkingZone1: [''],
      puissanceChoisieZone1: [null, [Validators.pattern('^\\d+(\\.\\d{1,2})?$')]],
      polygonsZone1: [null],
      azimutCoordinateZone1: [null]
    }, { validators: this.zoneSurfaceOrPowerValidator() })

    this.initTypePoseZone(
      'typePoseZone1',
      'deposeCandelabre',
      'poseCandelabre',
      'eclairageSousOmbriere',
      'descenteEpAlu',
      'nbAbattageArbres',
      'mlClotureSol',
      "nbPortailsSol",
      "mlGardeCorpsProvisoire",
      "mlLigneDeVie",
      "nbEchelleCrinoline",
      'surfaceZone1',
      'azimutZone1',
      'typeToitureZone1',
      'pvReadyZone1',
      'inclinaisonZone1',
      'typeVehiculeParkingZone1',
      'typePlaceParkingZone1',
      'puissanceChoisieZone1'
    )

    this.zone1Form.get('puissanceChoisieZone1')?.valueChanges.subscribe(() => {
      this.updateZoneValueValidators()
      this.zone1Form.updateValueAndValidity({ emitEvent: false })
    })
  }

  initTypePoseZone(
    typePose: string,
    deposeCandelabre: string,
    poseCandelabre: string,
    eclairageSousOmbriere: string,
    descenteEpAlu: string,
    nbAbattageArbres: string,
    mlClotureSol: string,
    nbPortailsSol: string,
    mlGardeCorpsProvisoire: string,
    mlLigneDeVie: string,
    nbEchelleCrinoline: string,
    surfaceZone: string,
    azimutZone: string,
    typeToitureZone: string,
    pvReadyZone: string,
    inclinaisonZone: string,
    typeVehiculeParkingZone: string,
    typePlaceParkingZone: string,
    puissanceChoisieZone: string
  ) {
    this.zone1Form.get(typePose).valueChanges.subscribe((typePoseZone) => {
      const constSurfaceZone = this.zone1Form.get(surfaceZone)
      const constAzimutZone = this.zone1Form.get(azimutZone)
      const constTypeToitureZone = this.zone1Form.get(typeToitureZone)
      const constPvReadyZone = this.zone1Form.get(pvReadyZone)
      const constInclinaisonZone = this.zone1Form.get(inclinaisonZone)
      const constTypeVehiculeParkingZone = this.zone1Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone1Form.get(typePlaceParkingZone)

      this.geomapDomainService.currentZone.typeVehiculeParking = '';
      this.geomapDomainService.activateMeasureAreaTool()

      if (typePoseZone) {
        this.updateZoneValueValidators()

        if (typePoseZone === 'TOITURE') {
          constTypeToitureZone.setValidators([Validators.required])
          constPvReadyZone.setValidators([Validators.required])
          constInclinaisonZone.setValidators([
            Validators.required,
            Validators.pattern('^\\d+$'),
            this.inclinaisonValidator()
          ])          
          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()

          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
        if (typePoseZone === 'OMBRIERE') {
          constTypeVehiculeParkingZone.setValidators([Validators.required])
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()

          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
        }

        if (typePoseZone === 'SOL') {
          constTypeToitureZone.setValidators([Validators.nullValidator])
          constPvReadyZone.setValidators([Validators.nullValidator])
          constInclinaisonZone.setValidators([Validators.nullValidator])
          constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
          constTypePlaceParkingZone.setValidators([Validators.nullValidator])

          constTypeToitureZone.reset()
          constPvReadyZone.reset()
          constInclinaisonZone.reset()
          constTypeVehiculeParkingZone.reset()
          constTypePlaceParkingZone.reset()
        }
      } else {
        constSurfaceZone.setValidators([Validators.pattern('^\\d+(\\.\\d{1,2})?$')])
        constAzimutZone.setValidators([Validators.pattern('^-?(180|\\d{1,2}|1[0-7]\\d)$')])
        constTypeToitureZone.setValidators([Validators.nullValidator])
        constPvReadyZone.setValidators([Validators.nullValidator])
        constInclinaisonZone.setValidators([Validators.nullValidator])
        constTypeVehiculeParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])

        constSurfaceZone.reset()
        constAzimutZone.reset()
        constTypeToitureZone.reset()
        constPvReadyZone.reset()
        constInclinaisonZone.reset()
        constTypeVehiculeParkingZone.reset()
        constTypePlaceParkingZone.reset()
      }

      constSurfaceZone.updateValueAndValidity({ emitEvent: false })
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone1Form.get(typeVehiculeParkingZone).valueChanges.subscribe((typeVehiculeParking) => {
      const constTypeVehiculeParkingZone = this.zone1Form.get(typeVehiculeParkingZone)
      const constTypePlaceParkingZone = this.zone1Form.get(typePlaceParkingZone)
      constTypeVehiculeParkingZone.setValidators([Validators.required])
      if (typeVehiculeParking === 'VL') {
        constTypePlaceParkingZone.setValidators([Validators.required])
      } else {
        constTypePlaceParkingZone.setValidators([Validators.nullValidator])
        constTypePlaceParkingZone.reset()
      }
    })

    this.zone1Form.get(typeToitureZone).valueChanges.subscribe((typeToiture) => {
      const constAzimutZone = this.zone1Form.get(azimutZone)
      this.zone1Form.get(inclinaisonZone).updateValueAndValidity();
      if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })

    this.zone1Form.get(azimutZone).valueChanges.subscribe((azimut) => {
      const constAzimutZone = this.zone1Form.get(azimutZone)
      const constTypeToitureZone = this.zone1Form.get(typeToitureZone)
      if (constTypeToitureZone.value === 'BETON' || constTypeToitureZone.value === 'TAN_ETANCHE') {
        this.azimutLabel = "Azimut (compris entre -90 et 90, SUD = 0)"
        this.updateZoneValueValidators()
      } else {
        this.azimutLabel = "Azimut (compris entre -180 et 180, SUD = 0)"
        this.updateZoneValueValidators()
      }
      constAzimutZone.updateValueAndValidity({ emitEvent: false })
    })
  }

  onPuissanceChoisieFocus(event: FocusEvent): void {
    if (!this.hasDrawnSurface()) {
      return
    }

    const input = event.target as HTMLInputElement | null
    input?.blur()
    const modalRef = this.modalService.open(ConfirmationModalComponent)
    modalRef.componentInstance.message = `L'ajout d'une puissance dans "Puissance choisie" entrainera la suppression des surfaces dessinées précédemment.`
    modalRef.componentInstance.confirmation = 'Voulez vous continuer ?'
    modalRef.result.then((confirmed: boolean) => {
      if (!confirmed) {
        return
      }
      this.geomapDomainService.clearZoneDrawings(0)
      this.zone1Form.patchValue({
        surfaceZone1: null,
        polygonsZone1: null,
      }, { emitEvent: false })
      this.updateZoneValueValidators()
      setTimeout(() => input?.focus())
    })
  }

  selectVehicule(typeVehicule: string, typePlace: string | null): void {
    const oldTypeVehicule = this.zone1Form.get('typeVehiculeParkingZone1')?.value;
    const oldTypePlace = this.zone1Form.get('typePlaceParkingZone1')?.value;

    const newSelection = `${typeVehicule}_${typePlace}`;
    const oldSelection = `${oldTypeVehicule}_${oldTypePlace}`;

    if (newSelection !== oldSelection && this.geomapDomainService.currentZone.vectorLayer.getSource().getFeatures().length > 0) {
      const modalRef = this.modalService.open(ConfirmationModalComponent);

      modalRef.result.then((confirmed : any) => {
        if (confirmed) {
          this.geomapDomainService.resetDessinModeValorisation();
          this.applySelection(typeVehicule, typePlace);
        }
      });
    } else {
      this.applySelection(typeVehicule, typePlace);
    }
  }

  private applySelection(typeVehicule: string, typePlace: string | null): void {
    this.zone1Form.get('typeVehiculeParkingZone1')?.setValue(typeVehicule);
    this.zone1Form.get('typePlaceParkingZone1')?.setValue(typePlace);
    this.geomapDomainService.currentZone.typeVehiculeParking = this.zone1Form.get('typeVehiculeParkingZone1')?.value + '_' + this.zone1Form.get('typePlaceParkingZone1')?.value ;
    this.geomapDomainService.activateMeasureAreaTool()
  }
  
  isVLSelected(place: string): boolean {
    return this.zone1Form.get('typeVehiculeParkingZone1')?.value === 'VL' &&
           this.zone1Form.get('typePlaceParkingZone1')?.value === place;
  }

  inclinaisonValidator() {
  return (control: any) => {
    if (!control || !this.zone1Form) return null;

    const inclinaison = control.value;
    const typeToiture = this.zone1Form.get('typeToitureZone1')?.value;

    if (inclinaison == null || inclinaison === '') return null;

    if (typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE') {
      if (inclinaison > 3) {
        return { inclinaisonMax: true };
      }
    }

    if (typeToiture === 'BAC_ACIER_SEC_EXISTANT' || typeToiture === 'BAC_ACIER_SEC_NEUF') {
      if (inclinaison <= 3) {
        return { inclinaisonMin: true };
      }
    }

    return null;
  };
}

private updateZoneValueValidators(): void {
  const typePose = this.zone1Form.get('typePoseZone1')?.value
  const puissanceChoisie = this.zone1Form.get('puissanceChoisieZone1')?.value
  const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
  const surfaceControl = this.zone1Form.get('surfaceZone1')
  const azimutControl = this.zone1Form.get('azimutZone1')
  const typeToiture = this.zone1Form.get('typeToitureZone1')?.value

  surfaceControl?.setValidators(hasPower || !typePose ? [Validators.pattern('^\\d+(\\.\\d{1,2})?$')] : [Validators.required, Validators.pattern('^\\d+(\\.\\d{1,2})?$')])

  const azimutPattern =
    typeToiture === 'BETON' || typeToiture === 'TAN_ETANCHE'
      ? '^-?(90|[0-8]?[0-9])$'
      : '^-?(180|\\d{1,3}|1[0-7]\\d)$'

  azimutControl?.setValidators(hasPower || !typePose ? [Validators.pattern(azimutPattern)] : [Validators.required, Validators.pattern(azimutPattern)])
  surfaceControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
  azimutControl?.updateValueAndValidity({ emitEvent: false, onlySelf: true })
}

private zoneSurfaceOrPowerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const typePose = control.get('typePoseZone1')?.value
    if (!typePose) {
      return null
    }
    const surface = control.get('surfaceZone1')?.value
    const puissanceChoisie = control.get('puissanceChoisieZone1')?.value
    const hasSurface = surface !== null && surface !== undefined && surface !== ''
    const hasPower = puissanceChoisie !== null && puissanceChoisie !== undefined && puissanceChoisie !== ''
    return hasSurface || hasPower ? null : { surfaceOrPowerRequired: true }
  }
}

private hasDrawnSurface(): boolean {
  const polygons = this.zone1Form.get('polygonsZone1')?.value as unknown[] | null
  const surface = this.zone1Form.get('surfaceZone1')?.value
  return !!(polygons && polygons.length > 0) || !!surface
}
}
